import { useQuery } from '@tanstack/react-query';
import { useSession } from '@supabase/auth-helpers-react';
import { supabase } from '../lib/supabase';

interface SubscriptionStatus {
  hasFamilySync: boolean;
  subscriptionPlan: string | null;
  subscriptionStatus: string | null;
  isLoading: boolean;
  refetch: () => void;
}

/**
 * Hook to check if the current user has an active Family Sync subscription
 */
export function useSubscription(): SubscriptionStatus {
  const session = useSession();
  const userId = session?.user?.id;

  const { data, isLoading, refetch, isError } = useQuery(
    ['subscription', userId],
    async () => {
      if (!userId) {
        return {
          hasFamilySync: false,
          subscriptionPlan: null,
          subscriptionStatus: null,
        };
      }

      // Fetch profile subscription info
      // Wrap in Promise.race with timeout to prevent hanging
      let profileResult;
      try {
        const queryPromise = supabase
          .from('profiles')
          .select('subscription_plan, subscription_status, lifetime_access')
          .eq('id', userId)
          .single();

        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Subscription check timeout')), 10000)
        );

        profileResult = await Promise.race([queryPromise, timeoutPromise]) as { data: any; error: any };
      } catch (error) {
        console.error('Error or timeout fetching profile subscription:', error);
        return {
          hasFamilySync: false,
          subscriptionPlan: null,
          subscriptionStatus: null,
        };
      }

      const { data: profile, error: profileError } = profileResult;

      if (profileError) {
        console.error('Error fetching profile subscription:', profileError);
        return {
          hasFamilySync: false,
          subscriptionPlan: null,
          subscriptionStatus: null,
        };
      }

      // Check for owner subscription plans in camps table
      // Wrap in timeout to prevent hanging
      let ownerCamps = null;
      let campsError = null;
      try {
        const campsQueryPromise = supabase
          .from('camps')
          .select('plan_id, claimed, owner_id')
          .eq('owner_id', userId)
          .eq('claimed', true)
          .in('plan_id', ['trailhead', 'campfire', 'summit', 'everest']);

        const campsTimeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Camps query timeout')), 10000)
        );

        const campsResult = await Promise.race([campsQueryPromise, campsTimeoutPromise]) as { data: any; error: any };
        ownerCamps = campsResult.data;
        campsError = campsResult.error;
      } catch (error) {
        console.error('Error or timeout fetching owner camps:', error);
        // Continue with ownerCamps = null
      }

      if (campsError) {
        console.error('Error fetching owner camps:', campsError);
      }

      // Check for Family Sync subscription (parent recurring plan)
      const hasFamilySyncSubscription = 
        profile?.subscription_plan === 'family-sync' && 
        profile?.subscription_status === 'active';
      
      // Check for lifetime access (any LTD plan - parents or owners)
      const hasLifetimeAccess = 
        profile?.lifetime_access === true ||
        (profile?.subscription_plan?.startsWith('ltd-') && profile?.subscription_status === 'lifetime');

      // Check for owner subscription plans (trailhead, campfire, summit, everest)
      const hasOwnerSubscription = 
        ownerCamps && ownerCamps.length > 0;

      // Grant Family Sync access if user has ANY paid plan
      const hasFamilySync = hasFamilySyncSubscription || hasLifetimeAccess || hasOwnerSubscription;

      // Debug logging
      console.log('Subscription check:', {
        userId,
        subscriptionPlan: profile?.subscription_plan,
        subscriptionStatus: profile?.subscription_status,
        lifetimeAccess: profile?.lifetime_access,
        ownerCamps: ownerCamps?.map(c => c.plan_id),
        hasFamilySyncSubscription,
        hasLifetimeAccess,
        hasOwnerSubscription,
        hasFamilySync,
        fullProfile: profile
      });

      return {
        hasFamilySync,
        subscriptionPlan: profile?.subscription_plan || null,
        subscriptionStatus: profile?.subscription_status || null,
      };
    },
    {
      enabled: !!userId,
      staleTime: 0, // Always consider data stale - refetch immediately when requested
      cacheTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: true, // Refetch when user returns to tab
      refetchOnMount: true, // Always refetch on mount
      refetchOnReconnect: true, // Refetch when network reconnects
      retry: 1, // Only retry once on failure
      retryDelay: 1000, // Wait 1 second before retry
      // Add timeout to prevent hanging
      networkMode: 'online', // Only run when online
    }
  );

  // If there's an error, don't keep loading - return default values
  const isLoadingSafe = isError ? false : isLoading;

  return {
    hasFamilySync: data?.hasFamilySync || false,
    subscriptionPlan: data?.subscriptionPlan || null,
    subscriptionStatus: data?.subscriptionStatus || null,
    isLoading: isLoadingSafe,
    refetch, // Expose refetch function
  };
}

